/**
 * Início guiado do checklist — uma decisão por tela, no celular.
 *
 * Checklist de Frota: operação → motorista → cavalo → implemento → KM → iniciar.
 * Checklist de Manutenção (`?tipo=manutencao`): template → veículo → KM → iniciar.
 * Checklist Diário (`?tipo=diario`): motorista → cavalo → KM → iniciar. O template
 * é o único publicado do tipo `daily`; a carreta vinculada entra sozinha.
 *
 * O SIAG preenche o que já sabe: o template sai do implemento quando o cadastro
 * informa o tipo; o último KM vem com a fonte. Quando o cadastro NÃO informa o
 * implemento (hoje, sempre), a escolha é controlada — três opções — e o servidor
 * registra que foi manual, o que alimenta a lista de melhoria cadastral do gestor.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MaintenanceChecklistKindDto,
  MaintenanceDriverSearchDto,
  MaintenanceImplementTypeDto,
  MaintenanceTemplateSummaryDto,
  MaintenanceVehicleContextDto,
} from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { EmptyState } from "../../components/EmptyState";
import { IconCheck, IconChevronLeft, IconTruck, IconUser, IconWrench } from "../../components/icons";
import { portalPaths } from "../../features/portal/portal-nav";
import { useCapabilities } from "../../features/maintenance/useCapabilities";

const IMPLEMENTOS: Array<{ value: MaintenanceImplementTypeDto; label: string }> = [
  { value: "sider", label: "Sider" },
  { value: "graneleiro", label: "Graneleiro" },
  { value: "grade_baixa", label: "Grade Baixa" },
];

/** Mesmo teto da API (`MAX_ODOMETER_JUMP_KM`) — aqui só para avisar antes. */
const SALTO_MAXIMO_KM = 20_000;

type Passo = "operacao" | "template" | "movimento" | "motorista" | "cavalo" | "implemento" | "km";

export function PortalStartChecklistPage() {
  const [params] = useSearchParams();
  const manutencao = params.get("tipo") === "manutencao";
  const diario = params.get("tipo") === "diario";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, loading: carregandoPermissao } = useCapabilities();

  // Entrega/devolução já dizem saída/retorno; Diário e Manutenção perguntam.
  const passos: Passo[] = manutencao
    ? ["template", "movimento", "cavalo", "km"]
    : diario
      ? ["movimento", "motorista", "cavalo", "km"]
      : ["operacao", "motorista", "cavalo", "implemento", "km"];
  const [i, setI] = useState(0);
  const passo = passos[i]!;

  const [kind, setKind] = useState<MaintenanceChecklistKindDto | null>(null);
  const [movimento, setMovimento] = useState<"departure" | "arrival" | null>(null);
  const [templateManutencao, setTemplateManutencao] = useState<string | null>(null);
  const [motorista, setMotorista] = useState<{ id: string; name: string } | null>(null);
  const [semMotorista, setSemMotorista] = useState(false);
  const [cavalo, setCavalo] = useState<MaintenanceVehicleContextDto | null>(null);
  const [carreta, setCarreta] = useState<MaintenanceVehicleContextDto | null>(null);
  const [implemento, setImplemento] = useState<MaintenanceImplementTypeDto | null>(null);
  const [trocandoImplemento, setTrocandoImplemento] = useState(false);
  const [km, setKm] = useState("");
  const [motivoKm, setMotivoKm] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ["maintenance", "templates"],
    queryFn: () => api.get<{ items: MaintenanceTemplateSummaryDto[] }>("/maintenance/templates"),
  });

  const implementoConhecido = carreta?.implementType ?? cavalo?.implementType ?? null;
  const origemConhecida = carreta?.implementType
    ? carreta.implementTypeSource
    : cavalo?.implementType
      ? cavalo.implementTypeSource
      : null;
  const implementoEfetivo = implemento ?? implementoConhecido;

  /**
   * A carreta vinculada ao cavalo no cadastro do ERP entra sozinha — é o vínculo
   * que o SIAG já tem (4.161 conjuntos em produção). O usuário pode trocar.
   */
  const [carretaDoCadastro, setCarretaDoCadastro] = useState<string | null>(null);
  useEffect(() => {
    const vinculada = cavalo?.trailerPlate;
    if (!vinculada || carreta) return;
    let vivo = true;
    api.get<MaintenanceVehicleContextDto>(`/maintenance/vehicle-context?plate=${encodeURIComponent(vinculada)}`)
      .then((ctx) => { if (vivo) { setCarreta(ctx); setCarretaDoCadastro(ctx.plate); } })
      .catch(() => undefined);
    return () => { vivo = false; };
  }, [cavalo?.trailerPlate, carreta]);

  const template = useMemo(() => {
    const lista = (templates.data?.items ?? []).filter((t) => t.publishedVersion);
    if (manutencao) return lista.find((t) => t.id === templateManutencao) ?? null;
    // O Diário da frota; Diários de outros tipos de veículo (empilhadeira…) vão pelo "Outro checklist".
    if (diario) return lista.find((t) => t.kind === "daily" && !t.vehicleType) ?? null;
    return lista.find((t) => t.kind === kind && t.implementType === implementoEfetivo) ?? null;
  }, [templates.data, manutencao, diario, templateManutencao, kind, implementoEfetivo]);

  const ultimo = cavalo?.lastOdometer ?? null;
  const kmNum = km === "" ? null : Number(km);
  const kmMenor = kmNum != null && ultimo != null && kmNum < ultimo.km;
  const kmSalto = kmNum != null && ultimo != null && kmNum - ultimo.km > SALTO_MAXIMO_KM;

  const iniciar = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/maintenance/runs", {
        templateId: template!.id,
        vehiclePlate: cavalo!.plate,
        trailerPlate: carreta?.plate ?? null,
        driverId: motorista?.id ?? null,
        odometerKm: kmNum,
        odometerOverrideReason: kmMenor ? motivoKm || null : null,
        movement: movimento,
      }),
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      navigate(portalPaths.run(run.id), { replace: true });
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível iniciar o checklist."),
  });

  const podeAvancar: Record<Passo, boolean> = {
    operacao: kind !== null,
    template: templateManutencao !== null,
    movimento: movimento !== null,
    motorista: motorista !== null || semMotorista,
    cavalo: cavalo !== null,
    implemento: implementoEfetivo !== null && template !== null,
    km: kmNum !== null && !kmSalto && (!kmMenor || motivoKm.trim() !== "") && template !== null,
  };

  if (!carregandoPermissao && !can("checklist.execute")) {
    return (
      <EmptyState
        title="Sem permissão para executar checklist"
        description="Seu perfil permite consultar. Peça ao gestor para liberar a execução de checklists."
        action={<Link className="btn-secondary" to={portalPaths.home}>Voltar ao início</Link>}
      />
    );
  }

  const titulo = manutencao ? "Checklist de Manutenção" : diario ? "Checklist Diário" : "Checklist de Frota";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button
          className="btn-ghost -ml-3 min-h-[44px]"
          onClick={() => (i === 0 ? navigate(-1) : setI(i - 1))}
          aria-label="Voltar"
        >
          <IconChevronLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900 leading-tight">{titulo}</h1>
          <p className="text-xs text-gray-500">Passo {i + 1} de {passos.length}</p>
        </div>
      </div>

      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full bg-tm-500 transition-all" style={{ width: `${((i + 1) / passos.length) * 100}%` }} />
      </div>

      {/* ── Operação ─────────────────────────────────────────────────────── */}
      {passo === "operacao" && (
        <Passo titulo="Qual operação?" ajuda="A sequência do checklist segue a operação.">
          {[
            { value: "delivery" as const, label: "Entrega de veículo (saída)", desc: "Conjunto saindo com o motorista." },
            { value: "return_delivery" as const, label: "Devolução de veículo (retorno)", desc: "Conjunto voltando, inclui EPIs. Fica ligada à última saída da placa." },
          ].map((o) => (
            <Opcao key={o.value} ativo={kind === o.value} onClick={() => setKind(o.value)} titulo={o.label} desc={o.desc} />
          ))}
        </Passo>
      )}

      {/* ── Saída ou retorno ──────────────────────────────────────────────── */}
      {passo === "movimento" && (
        <Passo titulo="Saída ou retorno?" ajuda="O retorno fica ligado à última saída desta placa, e a gestão compara as duas.">
          {[
            { value: "departure" as const, label: "Saída", desc: "O veículo vai sair para a viagem." },
            { value: "arrival" as const, label: "Retorno", desc: "O veículo está voltando da viagem." },
          ].map((o) => (
            <Opcao key={o.value} ativo={movimento === o.value} onClick={() => setMovimento(o.value)} titulo={o.label} desc={o.desc} />
          ))}
        </Passo>
      )}

      {/* ── Template de manutenção ───────────────────────────────────────── */}
      {passo === "template" && (
        <Passo titulo="Qual checklist?" ajuda="Inspeção técnica da oficina ou modelos por tipo de veículo.">
          {(() => {
            const lista = (templates.data?.items ?? [])
              .filter((t) => t.publishedVersion && (t.kind === "maintenance" || t.kind === "inspection" || (t.kind === "daily" && t.vehicleType)))
              // Agrupados pelo tipo de veículo; os da frota (sem tipo) primeiro.
              .sort((a, b) => (a.vehicleType?.name ?? "").localeCompare(b.vehicleType?.name ?? "") || a.name.localeCompare(b.name));
            if (templates.isLoading) return <p className="text-sm text-gray-500">Carregando…</p>;
            if (lista.length === 0) {
              return (
                <div className="card">
                  <EmptyState
                    compact
                    icon={<IconWrench className="w-5 h-5" />}
                    title="Nenhum checklist de manutenção publicado"
                    description="Os itens da inspeção técnica ainda serão definidos com a oficina. Quando forem publicados, aparecem aqui."
                  />
                </div>
              );
            }
            return lista.map((t, k) => (
              <div key={t.id}>
                {(k === 0 || lista[k - 1]!.vehicleType?.id !== t.vehicleType?.id) && (
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mt-2 mb-1">{t.vehicleType?.name ?? "Frota (cavalo + carreta)"}</p>
                )}
                <Opcao ativo={templateManutencao === t.id} onClick={() => setTemplateManutencao(t.id)}
                  titulo={t.name} desc={`${t.itemCount} itens · versão ${t.publishedVersion!.version}`} />
              </div>
            ));
          })()}
        </Passo>
      )}

      {/* ── Motorista ────────────────────────────────────────────────────── */}
      {passo === "motorista" && (
        <Passo titulo="Motorista" ajuda="Do cadastro de motoristas da empresa.">
          <BuscaMotorista
            selecionado={motorista}
            onSelecionar={(m) => { setMotorista(m); setSemMotorista(false); }}
          />
          <button
            className={`w-full text-left text-sm px-4 py-3 rounded-lg border min-h-[48px] ${
              semMotorista ? "border-tm-500 bg-tm-50 text-tm-700" : "border-surface-border bg-white text-gray-600"
            }`}
            onClick={() => { setSemMotorista(true); setMotorista(null); }}
          >
            Sem motorista vinculado
          </button>
        </Passo>
      )}

      {/* ── Cavalo ───────────────────────────────────────────────────────── */}
      {passo === "cavalo" && (
        <Passo titulo={manutencao ? "Veículo" : "Cavalo"} ajuda="Placa do cadastro de veículos da empresa.">
          <BuscaPlaca selecionado={cavalo} onSelecionar={setCavalo} todas={!!template?.vehicleType} />
          {cavalo && <ResumoVeiculo v={cavalo} />}
        </Passo>
      )}

      {/* ── Implemento ───────────────────────────────────────────────────── */}
      {passo === "implemento" && (
        <Passo titulo="Implemento" ajuda="Placa da carreta. O tipo define o checklist.">
          {carretaDoCadastro && carreta?.plate === carretaDoCadastro && (
            <p className="text-sm text-gray-600 bg-surface-muted rounded-lg px-3 py-2">
              Carreta <span className="font-mono">{carretaDoCadastro}</span> veio do cadastro, vinculada a este cavalo. Troque se for outra.
            </p>
          )}
          <BuscaPlaca
            selecionado={carreta}
            onSelecionar={(v) => { setCarreta(v); setImplemento(null); setTrocandoImplemento(false); setCarretaDoCadastro(null); }}
          />
          {implementoConhecido && !trocandoImplemento ? (
            <div className="card p-3 text-sm flex items-start gap-2 text-tm-700 bg-tm-50 border-tm-200">
              <IconCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <strong>{IMPLEMENTOS.find((x) => x.value === implementoConhecido)?.label}</strong>
                {origemConhecida === "cadastro" ? ", identificado pelo cadastro do ERP." : ", classificação usada da última vez nesta placa."}
                <button className="ml-1 underline" onClick={() => setTrocandoImplemento(true)}>trocar</button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-warning-700 bg-warning-50 rounded-lg px-3 py-2">
                O cadastro do ERP não informa o tipo deste implemento. Escolha abaixo: o SIAG guarda a escolha e não
                pergunta de novo para esta placa.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {IMPLEMENTOS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setImplemento(o.value)}
                    className={`min-h-[52px] rounded-lg border text-sm font-medium ${
                      implementoEfetivo === o.value ? "bg-tm-500 text-white border-tm-500" : "bg-white text-gray-700 border-surface-border"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </>
          )}
          {implementoEfetivo && (
            template ? (
              <p className="text-sm text-gray-600">
                Checklist: <strong>{template.name}</strong> · {template.itemCount} itens · v{template.publishedVersion?.version}
              </p>
            ) : (
              <p className="text-sm text-danger-600">Não há checklist publicado para esta operação e implemento.</p>
            )
          )}
        </Passo>
      )}

      {/* ── KM ───────────────────────────────────────────────────────────── */}
      {passo === "km" && (
        <Passo titulo="Quilometragem" ajuda="Leia o hodômetro do painel.">
          <div className="card p-4 text-sm">
            <p className="text-gray-500">Último KM conhecido</p>
            {ultimo ? (
              <>
                <p className="text-2xl font-semibold text-gray-900">{fmtNumber(ultimo.km)} km</p>
                <p className="text-xs text-gray-400">
                  {ultimo.sourceLabel}
                  {ultimo.readAt ? ` · ${new Date(ultimo.readAt).toLocaleDateString("pt-BR")}` : ""}
                </p>
              </>
            ) : (
              <p className="text-gray-400">sem leitura registrada para esta placa</p>
            )}
          </div>
          <label className="block">
            <span className="text-sm text-gray-600">KM atual</span>
            <input
              className="input w-full mt-1 text-lg"
              inputMode="numeric"
              placeholder={ultimo ? String(ultimo.km) : "KM do hodômetro"}
              value={km}
              onChange={(e) => { setKm(e.target.value.replace(/\D/g, "")); setErro(null); }}
            />
          </label>
          {kmSalto && (
            <p className="text-sm text-danger-600">
              {fmtNumber(kmNum! - ultimo!.km)} km acima do último conhecido. Confira o número (limite {fmtNumber(SALTO_MAXIMO_KM)} km).
            </p>
          )}
          {kmMenor && (
            <label className="block">
              <span className="text-sm text-warning-700">KM menor que o conhecido: informe o motivo</span>
              <input className="input w-full mt-1" placeholder="Ex.: hodômetro substituído" value={motivoKm}
                onChange={(e) => setMotivoKm(e.target.value)} />
            </label>
          )}
          {diario && !templates.isLoading && !template && (
            <p className="text-sm text-danger-600">O Checklist Diário ainda não foi publicado para esta empresa.</p>
          )}
          {template && (
            <div className="rounded-lg bg-surface-muted p-3 text-sm text-gray-600 space-y-0.5">
              <p><strong>{template.name}</strong></p>
              <p className="font-mono">{cavalo?.plate}{carreta ? ` + ${carreta.plate}` : ""}</p>
              {!manutencao && <p>{motorista?.name ?? "Sem motorista vinculado"}</p>}
            </div>
          )}
        </Passo>
      )}

      {erro && <div className="card p-3 border-danger-600/30 bg-danger-50 text-sm text-danger-600">{erro}</div>}

      {/* Rodapé fixo acima da navegação inferior */}
      <div className="fixed md:static bottom-[calc(56px+env(safe-area-inset-bottom))] inset-x-0 z-20 bg-white md:bg-transparent border-t md:border-0 border-surface-border p-3 md:p-0">
        <div className="max-w-3xl mx-auto">
          {passo === "km" ? (
            <button
              className="btn-primary w-full justify-center min-h-[52px] text-base"
              disabled={!podeAvancar.km || iniciar.isPending}
              onClick={() => iniciar.mutate()}
            >
              {iniciar.isPending ? "Iniciando…" : "Iniciar checklist"}
            </button>
          ) : (
            <button
              className="btn-primary w-full justify-center min-h-[52px] text-base"
              disabled={!podeAvancar[passo]}
              onClick={() => setI(i + 1)}
            >
              Continuar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Passo({ titulo, ajuda, children }: { titulo: string; ajuda?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 pb-20 md:pb-0">
      <div>
        <h2 className="text-base font-semibold text-gray-900">{titulo}</h2>
        {ajuda && <p className="text-sm text-gray-500">{ajuda}</p>}
      </div>
      {children}
    </section>
  );
}

function Opcao({ ativo, onClick, titulo, desc }: { ativo: boolean; onClick: () => void; titulo: string; desc?: string }) {
  return (
    <button
      onClick={onClick}
      className={`card w-full text-left p-4 min-h-[64px] flex items-center gap-3 transition-colors ${
        ativo ? "border-tm-500 ring-2 ring-tm-500/30 bg-tm-50" : "hover:bg-surface-muted"
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900">{titulo}</p>
        {desc && <p className="text-sm text-gray-500">{desc}</p>}
      </div>
      {ativo && <IconCheck className="w-5 h-5 text-tm-600" />}
    </button>
  );
}

function BuscaMotorista({
  selecionado, onSelecionar,
}: { selecionado: { id: string; name: string } | null; onSelecionar: (m: { id: string; name: string }) => void }) {
  const [q, setQ] = useState("");
  const lista = useQuery({
    queryKey: ["maintenance", "drivers", q],
    queryFn: () => api.get<MaintenanceDriverSearchDto>(`/maintenance/drivers?q=${encodeURIComponent(q)}`),
  });
  return (
    <div className="space-y-2">
      <input className="input w-full" placeholder="Buscar pelo nome" value={q} onChange={(e) => setQ(e.target.value)} />
      {lista.data?.scope === "all" && (
        <p className="text-xs text-warning-700">
          Nenhum motorista próprio no cadastro (admissão sem demissão). Mostrando o cadastro inteiro.
        </p>
      )}
      <div className="card divide-y divide-surface-border max-h-72 overflow-y-auto">
        {(lista.data?.items ?? []).map((m) => (
          <button key={m.id} onClick={() => onSelecionar(m)}
            className={`w-full text-left px-4 py-3 min-h-[48px] flex items-center gap-3 ${selecionado?.id === m.id ? "bg-tm-50" : "hover:bg-surface-muted"}`}>
            <IconUser className="w-4 h-4 text-gray-400" />
            <span className="flex-1 text-sm text-gray-800">{m.name}</span>
            {selecionado?.id === m.id && <IconCheck className="w-4 h-4 text-tm-600" />}
          </button>
        ))}
        {lista.data && lista.data.items.length === 0 && <p className="px-4 py-3 text-sm text-gray-500">Nenhum motorista encontrado.</p>}
      </div>
    </div>
  );
}

function BuscaPlaca({
  selecionado, onSelecionar, todas = false,
}: {
  selecionado: MaintenanceVehicleContextDto | null;
  onSelecionar: (v: MaintenanceVehicleContextDto | null) => void;
  /** modelo de outro tipo de veículo (empilhadeira…): busca no cadastro inteiro, não só na frota própria */
  todas?: boolean;
}) {
  const [q, setQ] = useState(selecionado?.plate ?? "");
  const [erro, setErro] = useState<string | null>(null);
  const sugestoes = useQuery({
    queryKey: ["maintenance", "vehicles", q, todas],
    queryFn: () => api.get<{ items: Array<{ plate: string; vehicleTypeName: string | null }> }>(`/maintenance/vehicles?q=${encodeURIComponent(q)}${todas ? "&scope=all" : ""}`),
    enabled: q.length >= 2 && q !== selecionado?.plate,
  });

  const escolher = async (plate: string) => {
    setErro(null);
    try {
      const ctx = await api.get<MaintenanceVehicleContextDto>(`/maintenance/vehicle-context?plate=${encodeURIComponent(plate)}`);
      setQ(ctx.plate);
      onSelecionar(ctx);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Placa não encontrada.");
      onSelecionar(null);
    }
  };

  return (
    <div className="space-y-2">
      <input
        className="input w-full uppercase font-mono text-lg"
        placeholder="ABC1D23"
        autoCapitalize="characters"
        value={q}
        onChange={(e) => { setQ(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")); if (selecionado) onSelecionar(null); }}
      />
      {q.length >= 2 && q !== selecionado?.plate && (sugestoes.data?.items.length ?? 0) > 0 && (
        <div className="card divide-y divide-surface-border max-h-60 overflow-y-auto">
          {sugestoes.data!.items.map((v) => (
            <button key={v.plate} onClick={() => escolher(v.plate)} className="w-full text-left px-4 py-3 min-h-[48px] flex items-center gap-3 hover:bg-surface-muted">
              <IconTruck className="w-4 h-4 text-gray-400" />
              <span className="font-mono font-medium">{v.plate}</span>
              {v.vehicleTypeName && <span className="text-xs text-gray-500">{v.vehicleTypeName}</span>}
            </button>
          ))}
        </div>
      )}
      {erro && <p className="text-sm text-danger-600">{erro}</p>}
    </div>
  );
}

function ResumoVeiculo({ v }: { v: MaintenanceVehicleContextDto }) {
  return (
    <div className="card p-3 text-sm flex items-center gap-3">
      <IconCheck className="w-5 h-5 text-tm-600" />
      <div>
        <p className="font-mono font-medium text-gray-900">{v.plate}</p>
        <p className="text-xs text-gray-500">
          {[v.brand, v.vehicleTypeName].filter(Boolean).join(" · ") || "tipo não informado no cadastro"}
          {v.lastOdometer ? ` · último KM ${fmtNumber(v.lastOdometer.km)}` : ""}
        </p>
      </div>
    </div>
  );
}
